/**
 * Context-aware suggestion boosting.
 *
 * When the user is on a specific page, we boost related navigation items
 * so they appear in the empty-state suggestions. This makes the command
 * palette feel "smart" without any AI — it understands what's nearby.
 */

/** Map of path prefixes → navigation item IDs that are contextually related */
const CONTEXT_MAP: Record<string, string[]> = {
    // Sales section
    '/sales': [
        'nav-sales-pipelines',
        'nav-sales-lead-outcomes',
        'nav-sales-email-templates',
        'nav-sales-sales-quotas',
        'nav-sales-activity-quotas',
        'nav-sales-products',
        'nav-sales-quote-overview',
        'nav-sales-invoice-overview',
        'nav-sales-scheduler',
    ],
    '/sales/pipelines': [
        'nav-sales-lead-outcomes',
        'nav-sales-sales-quotas',
        'nav-setup-custom-fields',
    ],
    '/sales/quote': [
        'nav-sales-quote-template',
        'nav-sales-products',
        'nav-sales-invoice-overview',
    ],
    '/sales/invoice': [
        'nav-sales-invoice-template',
        'nav-sales-products',
        'nav-sales-quote-overview',
    ],
    '/sales/product': ['nav-sales-product-categories', 'nav-sales-quote-overview'],

    // Marketing section
    '/marketing': [
        'nav-marketing-broadcasts',
        'nav-marketing-audiences',
        'nav-marketing-forms',
        'nav-marketing-landing-pages',
        'nav-marketing-analytics',
        'nav-marketing-drip-sequences',
        'nav-marketing-domains',
        'nav-marketing-sender-address',
    ],
    '/marketing/email': [
        'nav-marketing-broadcasts',
        'nav-marketing-newsletters',
        'nav-marketing-drip-sequences',
        'nav-marketing-email-templates',
        'nav-marketing-audiences',
        'nav-marketing-sender-address',
    ],
    '/marketing/sms': ['nav-marketing-sms-broadcasts', 'nav-marketing-sms-drip'],
    '/marketing/landing-pages': ['nav-marketing-domains', 'nav-marketing-analytics'],
    '/marketing/forms': ['nav-marketing-connected-forms', 'nav-marketing-landing-pages'],

    // Engagement / Inbox section
    '/inbox': [
        'nav-inbox-all',
        'nav-inbox-chat',
        'nav-inbox-sms',
        'nav-inbox-settings-chat',
        'nav-inbox-agents',
        'nav-inbox-chatbot-threads',
    ],
    '/inbox/settings': [
        'nav-inbox-settings-chat',
        'nav-inbox-settings-sms',
        'nav-inbox-settings-facebook',
        'nav-inbox-settings-whatsapp',
        'nav-inbox-agents',
    ],

    // Reports section
    '/reports': [
        'nav-reports-sales',
        'nav-reports-losses',
        'nav-reports-new-leads',
        'nav-reports-activity',
        'nav-reports-funnel',
        'nav-reports-forecast',
    ],

    // Settings section
    '/setup': [
        'nav-setup-users',
        'nav-setup-integrations',
        'nav-setup-custom-fields',
        'nav-setup-billing',
        'nav-setup-import',
        'nav-setup-security',
    ],
    '/my-account': [
        'nav-my-profile',
        'nav-my-notifications',
        'nav-my-calendar',
        'nav-my-email-sending',
        'nav-my-email-syncing',
    ],

    // Entity pages
    '/companies': ['nav-setup-custom-fields', 'nav-setup-company-types', 'nav-setup-tags'],
    '/people': ['nav-setup-custom-fields', 'nav-setup-tags', 'nav-setup-people-iq'],
    '/leads': [
        'nav-sales-pipelines',
        'nav-sales-lead-outcomes',
        'nav-setup-custom-fields',
        'nav-reports-sales',
    ],
};

/**
 * Given the current path, return an ordered list of navigation item IDs
 * that are contextually relevant. Matches the most specific prefix first.
 */
export function getContextualBoosts(currentPath: string): string[] {
    // Sort prefixes by length descending so more specific paths match first
    const prefixes = Object.keys(CONTEXT_MAP).sort((a, b) => b.length - a.length);

    for (const prefix of prefixes) {
        if (currentPath.startsWith(prefix)) {
            return CONTEXT_MAP[prefix] || [];
        }
    }

    return [];
}
